import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  RadiologistStudyMedicalHistoryError,
  RadiologistStudyMedicalHistoryRequest,
  RadiologistStudyMedicalHistoryResponse,
  StudyMedicalHistoryError,
  StudyMedicalHistoryRequest,
  StudyMedicalHistoryResponse,
} from '@/api/model';

const KEY = {
  GET_QUESTIONNAIRE_RESPONSE_MEDICAL_HISTORY: 'GET_QUESTIONNAIRE_RESPONSE_MEDICAL_HISTORY',
  GET_QUESTIONNAIRE_RESPONSE_RADIOLOGIST_MEDICAL_HISTORY:
    'GET_QUESTIONNAIRE_RESPONSE_RADIOLOGIST_MEDICAL_HISTORY',
} as const;

const request = {
  getMedicalHistory: async (
    { payload: { studyId } }: StudyMedicalHistoryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<StudyMedicalHistoryResponse>(
      `${ENDPOINTS.FCT.QUESTIONNAIRE_RESPONSE.STUDY}${studyId}/medical-history`,
      { signal },
    );

    return data;
  },
  getRadiologistMedicalHistory: async (
    { payload: { studyId } }: RadiologistStudyMedicalHistoryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<RadiologistStudyMedicalHistoryResponse>(
      `${ENDPOINTS.FCT.QUESTIONNAIRE_RESPONSE.STUDY}${studyId}/radiologist-medical-history`,
      { signal },
    );

    return data;
  },
};

const query = {
  useGetMedicalHistory: (
    { payload }: StudyMedicalHistoryRequest,
    config?: Partial<UseQueryOptions<StudyMedicalHistoryResponse, StudyMedicalHistoryError>>,
  ) =>
    useQuery<StudyMedicalHistoryResponse, StudyMedicalHistoryError>({
      queryFn: ({ signal }) => request.getMedicalHistory({ payload }, signal),
      queryKey: [KEY.GET_QUESTIONNAIRE_RESPONSE_MEDICAL_HISTORY, payload.studyId],
      ...config,
    }),
  useGetRadiologistMedicalHistory: (
    { payload }: RadiologistStudyMedicalHistoryRequest,
    config?: Partial<
      UseQueryOptions<RadiologistStudyMedicalHistoryResponse, RadiologistStudyMedicalHistoryError>
    >,
  ) =>
    useQuery<RadiologistStudyMedicalHistoryResponse, RadiologistStudyMedicalHistoryError>({
      queryFn: ({ signal }) => request.getRadiologistMedicalHistory({ payload }, signal),
      queryKey: [KEY.GET_QUESTIONNAIRE_RESPONSE_RADIOLOGIST_MEDICAL_HISTORY, payload.studyId],
      ...config,
    }),
};

export const questionnaireResponse = {
  KEY,
  query,
  request,
};
